/**
 * Go text/template codegen from Mapping Model + Blockly workspace.
 *
 * Emits Go text/template syntax with a curated Sprig-subset FuncMap:
 * replace, regexReplaceAll, trim, quote, lower, substr, int, plus host-bound
 * `handlebars` / `dict` for canvas VMS-Hbs and `decisionTable` / `sheetLookup`
 * for convert-time Sheets (ADR 0005 / #70).
 *
 * Execute context shape: { Parameters: defaults, Data: sourceInstance }.
 * Source queries become {{ index .Data "flat/path|value" }}.
 * JSONPath `$.['flat/key|value']` unwraps to a single Data index.
 * Defaults Map lookups become {{ .Parameters.Key }}.
 *
 * Full Blockly canvas walking (loops, expressions, JSON/XML/schema trees) lives
 * in `src/blockly/go_template_codegen.ts` and replaces this adapter when a
 * workspace snapshot is available.
 */
import type { ExprAst } from "../expression/mod.ts";
import type { MappingFunction, MappingModel } from "../../types/mod.ts";
import { GO_TEMPLATE_HELPERS_BLOB } from "./helpers/locations.ts";
import { parseExpression, isQuantifyCall } from "../expression/mod.ts";
import { valueFunctions } from "./user_functions.ts";
import { isRelativeAuthoringPath } from "../mapping_model/loops.ts";
import { namedLoopBinding } from "../named_loop_path.ts";
import {
  injectXmlnsOnOpenTag,
  wrapXmlCdata,
  xmlDeclarationLine,
  XML_ATTRIBUTES_INPUT,
  XML_CHILDREN_INPUT,
  XML_ROOT_INPUT,
  XML_TEXT_INPUT,
  type XmlNamespaceDecl,
} from "../xml_shape.ts";

export interface GoEmitContext {
  /** When set, relative xpath paths evaluate against the loop item (`.`). */
  loopVar?: string;
  /** Loop item name → Go variable (`$dag`). Inner names shadow. */
  loopBindings?: Readonly<Record<string, string>>;
  mappingFunctions?: MappingFunction[];
  fnParams?: Set<string>;
  /** Slot id → archetype id for archetype-root blocks whose field is still the at-code. */
  archetypeRootIds?: Map<string, string>;
}

export function createGoEmitContext(
  loopVar?: string,
  mappingFunctions?: MappingFunction[],
): GoEmitContext {
  return {
    ...(loopVar ? { loopVar } : {}),
    ...(mappingFunctions ? { mappingFunctions } : {}),
  };
}

export function generateGoTemplate(
  model: MappingModel,
  options?: { blocklyState?: unknown },
): string {
  const ctx = createGoEmitContext(undefined, model.functions);
  const lines: string[] = [
    `{{- /* Generated by intEHRgrator — Go text/template Conversion Script */ -}}`,
    `{{- /* Target: ${model.templateId || "free-form"} */ -}}`,
  ];
  const defines = emitGoFunctionDefines(model, ctx);
  if (defines) lines.push(defines);

  const body = options?.blocklyState
    ? generateFromBlockly(options.blocklyState, model)
    : generateFromSlots(model, ctx);
  const helperNames = ["sheetLookup", "decisionTable", "handlebars", "dict"].filter((name) =>
    body.includes(name)
  );
  if (helperNames.length) {
    lines.push(
      `{{- /* Helpers: ${helperNames.join(", ")} — ${GO_TEMPLATE_HELPERS_BLOB} */ -}}`,
    );
  }
  lines.push(body);

  return lines.join("\n");
}

export function emitGoFunctionDefines(model: MappingModel, ctx: GoEmitContext): string {
  const fns = valueFunctions(model.functions);
  if (!fns.length) return "";
  ctx.mappingFunctions = fns;
  const lines: string[] = [];
  for (const fn of fns) {
    const inner: GoEmitContext = { ...ctx, fnParams: new Set(fn.params) };
    const body = emitGoExpressionTemplate(parseExpression(fn.body!), inner);
    lines.push(`{{- define ${goQuote(fn.name)} -}}${body}{{- end -}}`);
  }
  return lines.join("\n");
}

function generateFromSlots(model: MappingModel, ctx: GoEmitContext): string {
  const lines: string[] = [];
  for (const slot of model.slots) {
    const ast = parseExpression(slot.expression);
    const goExpr = emitGoExpressionTemplate(ast, ctx);
    lines.push(`{{- /* ${slot.label ?? slot.slotId} */ -}}`);
    lines.push(goExpr);
  }
  return lines.join("\n");
}

function generateFromBlockly(blocklyState: unknown, _model: MappingModel): string {
  const snapshot = JSON.parse(JSON.stringify(blocklyState)) as {
    blocks?: { blocks?: unknown[] };
  };
  const blocks = snapshot?.blocks?.blocks;
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return generateFromSlots(_model);
  }
  return emitBlockTree(blocks).join("\n");
}

interface BlockInput {
  block?: BlockNode;
  shadow?: BlockNode;
}

export interface BlockNode {
  type: string;
  id?: string;
  fields?: Record<string, unknown>;
  inputs?: Record<string, BlockInput>;
  next?: { block?: BlockNode };
  extraState?: {
    attributes?: Array<{ name: string; value: string }>;
    childGroups?: string[];
    elseIfCount?: number;
    hasElse?: boolean;
    xmlAttributes?: string[];
    fields?: Array<{ name: string; xmlKind?: string }>;
    itemCount?: number;
    declaration?: boolean;
    version?: string;
    encoding?: string;
    standalone?: string;
    namespaces?: XmlNamespaceDecl[];
    extras?: string[];
  };
}

/** Blockly serializes unused default values as shadows; maps_get KEY is typically a shadow. */
export function inputChild(input: BlockInput | undefined): BlockNode | undefined {
  return input?.block ?? input?.shadow;
}

function serializeBlockNodeExpression(block: BlockNode | undefined): string | null {
  if (!block?.type) return null;
  switch (block.type) {
    case "text":
    case "text_code":
      return JSON.stringify(String(block.fields?.TEXT ?? ""));
    case "math_number":
      return String(block.fields?.NUM ?? 0);
    case "logic_boolean":
      return block.fields?.BOOL === "TRUE" ? "true" : "false";
    case "source_query":
    case "source_query_number":
      return `xpathString(${JSON.stringify(String(block.fields?.EXPRESSION ?? ""))})`;
    case "source_query_boolean":
      return `xpathBoolean(${JSON.stringify(String(block.fields?.EXPRESSION ?? ""))})`;
    case "source_query_node":
      return `xpathNode(${JSON.stringify(String(block.fields?.EXPRESSION ?? ""))})`;
    case "maps_create_empty":
      return "map()";
    case "maps_create_with": {
      const count = Number(block.extraState?.itemCount ?? 0);
      const parts: string[] = [];
      for (let i = 0; i < count; i++) {
        parts.push(JSON.stringify(String(block.fields?.[`KEY${i}`] ?? "")));
        const value = inputChild(block.inputs?.[`VAL${i}`]);
        parts.push(serializeBlockNodeExpression(value) ?? "null");
      }
      return `map(${parts.join(", ")})`;
    }
    case "text_handlebars": {
      const script = serializeBlockNodeExpression(inputChild(block.inputs?.SCRIPT)) ?? '""';
      const context = serializeBlockNodeExpression(inputChild(block.inputs?.CONTEXT)) ?? "map()";
      return `handlebars(${script}, ${context})`;
    }
    case "decision_table": {
      const name = String(block.fields?.NAME ?? "Decision1");
      const inputs = serializeBlockNodeExpression(inputChild(block.inputs?.INPUTS)) ?? "map()";
      const output = String(block.fields?.OUTPUT ?? "out");
      return `decision_table(${JSON.stringify(name)}, ${inputs}, ${JSON.stringify(output)})`;
    }
    case "sheet_lookup": {
      const name = String(block.fields?.NAME ?? "Sheet1");
      const col = serializeBlockNodeExpression(inputChild(block.inputs?.MATCH_COL)) ?? '""';
      const val = serializeBlockNodeExpression(inputChild(block.inputs?.MATCH_VAL)) ?? '""';
      const ret = serializeBlockNodeExpression(inputChild(block.inputs?.RETURN_COL));
      return ret
        ? `sheet_lookup(${JSON.stringify(name)}, ${col}, ${val}, ${ret})`
        : `sheet_lookup(${JSON.stringify(name)}, ${col}, ${val})`;
    }
    default:
      return null;
  }
}

export function emitBlockTree(blocks: unknown[], ctx: GoEmitContext = createGoEmitContext()): string[] {
  const lines: string[] = [];
  for (const block of blocks) {
    lines.push(...emitStatementChain(block as BlockNode, ctx));
  }
  return lines;
}

export function emitBlock(block: BlockNode, ctx: GoEmitContext = createGoEmitContext()): string[] {
  const lines: string[] = [];
  if (!block) return lines;

  switch (block.type) {
    case "xml_element":
    case "target_structure": {
      lines.push(...emitXmlOrSchemaElement(block, ctx));
      break;
    }
    case "xml_document": {
      lines.push(...emitXmlDocument(block, ctx));
      break;
    }
    case "xml_text": {
      lines.push(...emitXmlTextish(block, ctx));
      break;
    }
    case "xml_cdata": {
      lines.push(...emitXmlTextish(block, ctx));
      break;
    }
    case "xml_attribute": {
      break;
    }
    case "defaults_block":
    case "default_context_map":
    case "maps_create_with":
    case "variables_set": {
      break;
    }
    case "controls_if": {
      lines.push(...emitControlsIf(block, ctx));
      break;
    }
    case "include_when": {
      lines.push(...emitIncludeWhen(block, ctx));
      break;
    }
    case "for_each_source": {
      lines.push(...emitForEachSource(block, ctx));
      break;
    }
    case "for_each_list": {
      lines.push(...emitForEachList(block, ctx));
      break;
    }
    case "json_object":
    case "json_array": {
      lines.push(...emitJsonStructure(block, ctx));
      break;
    }
    case "conversion_start": {
      break;
    }
    case "text_document": {
      const value = inputChild(block.inputs?.VALUE);
      if (value) lines.push(...emitValueExpression(value, ctx));
      break;
    }
    case "text":
    case "text_code": {
      lines.push(String(block.fields?.TEXT ?? ""));
      break;
    }
    case "text_handlebars": {
      const serialized = serializeBlockNodeExpression(block);
      if (serialized) {
        lines.push(emitGoExpressionTemplate(parseExpression(serialized), ctx));
      }
      break;
    }
    case "source_query":
    case "source_query_number":
    case "source_query_boolean":
    case "source_query_node": {
      const expr = String(block.fields?.EXPRESSION ?? "");
      lines.push(emitGoExpressionTemplate(
        parseExpression(`xpathString(${JSON.stringify(expr)})`),
        ctx,
      ));
      break;
    }
    case "maps_get": {
      const name = String(block.fields?.NAME ?? "defaults");
      const key = inputChild(block.inputs?.KEY);
      const keyStr = key ? extractTextValue(key) : "";
      lines.push(emitGoExpressionTemplate(
        parseExpression(`maps_get(${JSON.stringify(name)}, ${JSON.stringify(keyStr)})`),
        ctx,
      ));
      break;
    }
    default:
      if (block.type.startsWith("schema_")) {
        lines.push(...emitXmlOrSchemaElement(block, ctx));
      } else if (block.type.startsWith("json_")) {
        lines.push(...emitJsonStructure(block, ctx));
      } else {
        lines.push(`{{- /* unsupported block: ${block.type} */ -}}`);
      }
  }

  return lines;
}

export function emitStatementChain(
  block: BlockNode,
  ctx: GoEmitContext = createGoEmitContext(),
): string[] {
  const lines: string[] = [];
  let current: BlockNode | undefined = block;
  while (current) {
    lines.push(...emitBlock(current, ctx));
    current = current.next?.block;
  }
  return lines;
}

export function emitValueExpression(
  block: BlockNode,
  ctx: GoEmitContext = createGoEmitContext(),
): string[] {
  switch (block.type) {
    case "xml_text":
    case "xml_cdata":
      return emitXmlTextish(block, ctx);
    case "text":
      return [String(block.fields?.TEXT ?? "")];
    case "text_code":
      return [String(block.fields?.TEXT ?? "")];
    case "text_handlebars": {
      const serialized = serializeBlockNodeExpression(block);
      if (serialized) {
        return [emitGoExpressionTemplate(parseExpression(serialized), ctx)];
      }
      return [`{{- /* value: text_handlebars */ -}}`];
    }
    case "math_number":
      return [String(block.fields?.NUM ?? "0")];
    case "logic_boolean":
      return [block.fields?.BOOL === "TRUE" ? "true" : "false"];
    case "source_query":
    case "source_query_number":
    case "source_query_boolean":
    case "source_query_node": {
      const expr = String(block.fields?.EXPRESSION ?? "");
      return [emitGoExpressionTemplate(
        parseExpression(`xpathString(${JSON.stringify(expr)})`),
        ctx,
      )];
    }
    case "maps_get": {
      const name = String(block.fields?.NAME ?? "defaults");
      const key = inputChild(block.inputs?.KEY);
      const keyStr = key ? extractTextValue(key) : "";
      return [emitGoExpressionTemplate(
        parseExpression(`maps_get(${JSON.stringify(name)}, ${JSON.stringify(keyStr)})`),
        ctx,
      )];
    }
    case "text_changeCase": {
      const inner = inputChild(block.inputs?.TEXT);
      const caseType = String(block.fields?.CASE ?? "LOWERCASE");
      if (inner) {
        const innerExpr = emitValueExpression(inner, ctx).join("");
        if (caseType === "LOWERCASE") return [`{{ ${stripDelimiters(innerExpr)} | lower }}`];
        if (caseType === "UPPERCASE") return [`{{ ${stripDelimiters(innerExpr)} | upper }}`];
      }
      return inner ? emitValueExpression(inner, ctx) : [];
    }
    case "text_trim": {
      const inner = inputChild(block.inputs?.TEXT);
      if (inner) {
        const innerExpr = emitValueExpression(inner, ctx).join("");
        return [`{{ ${stripDelimiters(innerExpr)} | trim }}`];
      }
      return [];
    }
    default: {
      const fromSerialized = emitValueFromSerialized(block, ctx);
      if (fromSerialized) return fromSerialized;
      return [`{{- /* value: ${block.type} */ -}}`];
    }
  }
}

function emitValueFromSerialized(block: BlockNode, ctx: GoEmitContext): string[] | null {
  const serialized = serializeBlockNodeExpression(block) ?? blockNodeToExpression(block);
  if (!serialized) return null;
  return [emitGoExpressionTemplate(parseExpression(serialized), ctx)];
}

/** Best-effort Mapping Expression serialization for Blockly JSON nodes (JSON walker). */
function blockNodeToExpression(block: BlockNode): string | null {
  switch (block.type) {
    case "text":
    case "text_code":
      return JSON.stringify(String(block.fields?.TEXT ?? ""));
    case "math_number":
      return String(block.fields?.NUM ?? 0);
    case "logic_boolean":
      return block.fields?.BOOL === "TRUE" ? "true" : "false";
    case "source_query":
    case "source_query_number":
    case "source_query_boolean":
    case "source_query_node": {
      const expr = String(block.fields?.EXPRESSION ?? "");
      const fn = block.type === "source_query_number" ? "xpathNumber"
        : block.type === "source_query_boolean" ? "xpathBoolean"
        : block.type === "source_query_node" ? "xpathNode"
        : "xpathString";
      return `${fn}(${JSON.stringify(expr)})`;
    }
    case "maps_get": {
      const name = String(block.fields?.NAME ?? "defaults");
      const key = inputChild(block.inputs?.KEY);
      const keyStr = key ? extractTextValue(key) : "";
      return `maps_get(${JSON.stringify(name)}, ${JSON.stringify(keyStr)})`;
    }
    case "text_trim": {
      const inner = inputChild(block.inputs?.TEXT);
      const expr = inner ? blockNodeToExpression(inner) : '""';
      return expr ? `trim(${expr})` : null;
    }
    case "text_changeCase": {
      const inner = inputChild(block.inputs?.TEXT);
      const expr = inner ? blockNodeToExpression(inner) ?? '""' : '""';
      const mode = String(block.fields?.CASE ?? "LOWERCASE");
      return `change_case(${expr}, ${JSON.stringify(mode)})`;
    }
    case "text_join": {
      const count = block.extraState?.itemCount ?? 2;
      const parts: string[] = [];
      for (let i = 0; i < count; i++) {
        const child = inputChild(block.inputs?.[`ADD${i}`]);
        parts.push(child ? blockNodeToExpression(child) ?? '""' : '""');
      }
      if (!parts.length) return '""';
      if (parts.length === 1) return parts[0]!;
      return `concat(${parts.join(", ")})`;
    }
    case "logic_ternary": {
      const cond = blockNodeToExpression(inputChild(block.inputs?.IF) ?? { type: "" });
      const thenV = blockNodeToExpression(inputChild(block.inputs?.THEN) ?? { type: "" });
      const elseV = blockNodeToExpression(inputChild(block.inputs?.ELSE) ?? { type: "" });
      return `if(${cond ?? "false"}, ${thenV ?? "null"}, ${elseV ?? "null"})`;
    }
    case "logic_compare": {
      const op = String(block.fields?.OP ?? "EQ");
      const a = blockNodeToExpression(inputChild(block.inputs?.A) ?? { type: "" });
      const b = blockNodeToExpression(inputChild(block.inputs?.B) ?? { type: "" });
      const fn = { EQ: "eq", NEQ: "ne", LT: "lt", LTE: "le", GT: "gt", GTE: "ge" }[op] ?? "eq";
      return `${fn}(${a ?? "null"}, ${b ?? "null"})`;
    }
    case "logic_operation": {
      const op = String(block.fields?.OP ?? "AND");
      const a = blockNodeToExpression(inputChild(block.inputs?.A) ?? { type: "" });
      const b = blockNodeToExpression(inputChild(block.inputs?.B) ?? { type: "" });
      const fn = op === "OR" ? "or" : "and";
      return `${fn}(${a ?? "false"}, ${b ?? "false"})`;
    }
    case "logic_negate": {
      const inner = blockNodeToExpression(inputChild(block.inputs?.BOOL) ?? { type: "" });
      return `not(${inner ?? "false"})`;
    }
    case "math_arithmetic": {
      const op = String(block.fields?.OP ?? "ADD");
      const a = blockNodeToExpression(inputChild(block.inputs?.A) ?? { type: "" });
      const b = blockNodeToExpression(inputChild(block.inputs?.B) ?? { type: "" });
      const symbol = { ADD: "+", MINUS: "-", MULTIPLY: "*", DIVIDE: "/" }[op] ?? "+";
      return `(${a ?? "0"} ${symbol} ${b ?? "0"})`;
    }
    case "variables_get":
      return `var(${JSON.stringify(String(block.fields?.VAR ?? "v"))})`;
    default:
      return null;
  }
}

function goLoopIdent(name: string): string {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) ? name : "item";
}

function withGoLoop(ctx: GoEmitContext, varName: string): { ident: string; ctx: GoEmitContext } {
  const ident = goLoopIdent(varName);
  return {
    ident,
    ctx: {
      ...ctx,
      loopVar: ident,
      loopBindings: { ...ctx.loopBindings, [varName]: `$${ident}` },
    },
  };
}

function goIndexChain(root: string, rest: string): string {
  let expr = root;
  for (const seg of rest.split(".").filter(Boolean)) {
    expr = `index ${expr} ${goQuote(seg)}`;
  }
  return expr;
}

function emitForEachSource(block: BlockNode, ctx: GoEmitContext): string[] {
  const varName = String(block.fields?.VAR ?? "item");
  const path = String(block.fields?.PATH ?? "");
  const rangeExpr = loopRangeExpr(path, ctx);
  const bound = withGoLoop(ctx, varName);
  const body = inputChild(block.inputs?.DO);
  const lines = [`{{- range $${bound.ident} := ${rangeExpr} }}`];
  if (body) lines.push(...emitStatementChain(body, bound.ctx));
  lines.push(`{{- end }}`);
  return lines;
}

function emitForEachList(block: BlockNode, ctx: GoEmitContext): string[] {
  const varName = String(block.fields?.VAR ?? "item");
  const list = inputChild(block.inputs?.LIST);
  const sourcePath = list && isSourceQueryNodeType(list.type)
    ? String(list.fields?.EXPRESSION ?? "")
    : "";
  if (sourcePath) return emitForEachSource({ ...block, fields: { ...block.fields, PATH: sourcePath } }, ctx);
  const listExpr = list
    ? stripDelimiters(emitValueExpression(list, ctx).join(""))
    : "index .Data \"\"";
  const body = inputChild(block.inputs?.DO);
  const bound = withGoLoop(ctx, varName);
  const lines = [`{{- range $${bound.ident} := ${listExpr} }}`];
  if (body) lines.push(...emitStatementChain(body, bound.ctx));
  lines.push(`{{- end }}`);
  return lines;
}

function isSourceQueryNodeType(type: string | undefined): boolean {
  return type === "source_query" || type === "source_query_number" ||
    type === "source_query_boolean" || type === "source_query_node";
}

function loopRangeExpr(path: string, ctx: GoEmitContext): string {
  const trimmed = path.trim();
  const named = namedLoopBinding(trimmed, ctx.loopBindings);
  if (named) {
    return named.rest === "." ? named.ident : goIndexChain(named.ident, named.rest);
  }
  if (trimmed.startsWith("$.")) {
    const body = trimmed.replace(/^\$\.?/, "").replace(/\[(\d+|\*)\]/g, ".$1");
    const segments = body.split(".").filter(Boolean);
    if (!segments.length) return "index .Data \"\"";
    let expr = ".Data";
    for (const seg of segments) {
      expr = `index ${expr} ${goQuote(seg)}`;
    }
    return expr;
  }
  if (ctx.loopVar && isRelativeAuthoringPath(trimmed)) {
    return `index . ${goQuote(trimmed)}`;
  }
  return `index .Data ${goQuote(trimmed)}`;
}

function emitJsonStructure(block: BlockNode, ctx: GoEmitContext): string[] {
  if (block.type === "json_array") {
    const items: string[] = [];
    const count = block.extraState?.itemCount ?? 0;
    for (let i = 0; i < count; i++) {
      const child = inputChild(block.inputs?.[`ADD${i}`]);
      if (child) items.push(...emitValueExpression(child, ctx));
    }
    return [`[${items.join(", ")}]`];
  }
  const props: string[] = [];
  for (const [key, value] of Object.entries(block.inputs ?? {})) {
    if (!key.startsWith("TARGET_") && !key.startsWith("SCHEMA_OPT_")) continue;
    const fieldName = key.replace(/^TARGET_|^SCHEMA_OPT_/, "");
    const child = inputChild(value);
    if (!child) continue;
    if (isXmlStructureBlockType(child.type) || child.type.startsWith("json_")) {
      props.push(`"${fieldName}": ${emitBlock(child, ctx).join("")}`);
      continue;
    }
    props.push(`"${fieldName}": ${emitValueExpression(child, ctx).join("")}`);
  }
  return [`{${props.join(", ")}}`];
}

function emitIncludeWhen(block: BlockNode, ctx: GoEmitContext): string[] {
  const cond = inputChild(block.inputs?.WHEN);
  const condStr = cond ? emitConditionExpression(cond, ctx) : "false";
  const lines = [`{{- if ${condStr} }}`];
  const body = inputChild(block.inputs?.DO);
  if (body) lines.push(...emitStatementChain(body, ctx));
  const elseBody = inputChild(block.inputs?.ELSE);
  if (elseBody) {
    lines.push(`{{- else }}`);
    lines.push(...emitStatementChain(elseBody, ctx));
  }
  lines.push(`{{- end }}`);
  return lines;
}

function emitControlsIf(block: BlockNode, ctx: GoEmitContext): string[] {
  const lines: string[] = [];
  const elseCount = block.extraState;
  const ifCount = (elseCount?.elseIfCount ?? 0) + 1;

  for (let i = 0; i < ifCount; i++) {
    const cond = inputChild(block.inputs?.[`IF${i}`]);
    const body = inputChild(block.inputs?.[`DO${i}`]);
    const keyword = i === 0 ? "if" : "else if";
    const condStr = cond ? emitConditionExpression(cond, ctx) : "true";
    lines.push(`{{- ${keyword} ${condStr} }}`);
    if (body) lines.push(...emitStatementChain(body, ctx));
  }

  if (elseCount?.hasElse) {
    const elseBody = inputChild(block.inputs?.ELSE);
    lines.push(`{{- else }}`);
    if (elseBody) lines.push(...emitStatementChain(elseBody, ctx));
  }

  lines.push(`{{- end }}`);
  return lines;
}

function emitConditionExpression(block: BlockNode, ctx: GoEmitContext): string {
  switch (block.type) {
    case "logic_compare": {
      const op = String(block.fields?.OP ?? "EQ");
      const a = inputChild(block.inputs?.A);
      const b = inputChild(block.inputs?.B);
      const aStr = a ? emitInlineValue(a, ctx) : '""';
      const bStr = b ? emitInlineValue(b, ctx) : '""';
      const goOp = { EQ: "eq", NEQ: "ne", LT: "lt", LTE: "le", GT: "gt", GTE: "ge" }[op] ?? "eq";
      return `${goOp} ${aStr} ${bStr}`;
    }
    case "logic_operation": {
      const op = String(block.fields?.OP ?? "AND").toLowerCase();
      const a = inputChild(block.inputs?.A);
      const b = inputChild(block.inputs?.B);
      const aStr = a ? `(${emitConditionExpression(a, ctx)})` : "true";
      const bStr = b ? `(${emitConditionExpression(b, ctx)})` : "true";
      return `${op} ${aStr} ${bStr}`;
    }
    case "logic_negate": {
      const inner = inputChild(block.inputs?.BOOL);
      return inner ? `not (${emitConditionExpression(inner, ctx)})` : "true";
    }
    default:
      return emitInlineValue(block, ctx);
  }
}

function emitInlineValue(block: BlockNode, ctx: GoEmitContext): string {
  const serialized = blockNodeToExpression(block);
  if (serialized) {
    const ast = parseExpression(serialized);
    return emitGoExpr(ast, ctx);
  }
  return `""`;
}

function emitXmlOrSchemaElement(block: BlockNode, ctx: GoEmitContext): string[] {
  if (block.type === "xml_element") return emitXmlElement(block, ctx);
  const tag = xmlTagName(block);
  const xmlAttributes = new Set([
    ...(block.extraState?.xmlAttributes ?? []),
    ...(block.extraState?.fields ?? [])
      .filter((field) => field.xmlKind === "attribute")
      .map((field) => field.name),
  ]);
  const attrParts: string[] = [...emitStaticAttributes(block, ctx)];
  const inner: string[] = [];
  const inputs = block.inputs ?? {};
  for (const [key, value] of Object.entries(inputs)) {
    if (!key.startsWith("TARGET_") && !key.startsWith("SCHEMA_OPT_")) continue;
    const fieldName = key.replace(/^TARGET_|^SCHEMA_OPT_/, "");
    const child = inputChild(value);
    if (!child) continue;
    if (xmlAttributes.has(fieldName) || child.type === "xml_attribute") {
      const val = emitValueExpression(child, ctx).join("");
      attrParts.push(` ${fieldName}="${val}"`);
      continue;
    }
    if (isXmlStructureBlockType(child.type)) {
      inner.push(...emitStatementChain(child, ctx));
      continue;
    }
    inner.push(`<${xmlName(fieldName)}>`, ...emitValueExpression(child, ctx), `</${xmlName(fieldName)}>`);
  }
  const attrs = attrParts.join("");
  if (inner.length) return [`<${tag}${attrs}>`, ...inner, `</${tag}>`];
  return [`<${tag}${attrs}></${tag}>`];
}

function isXmlStructureBlockType(type: string): boolean {
  return type === "xml_element" || type === "target_structure" || type.startsWith("schema_") ||
    type === "controls_if" || type === "include_when" ||
    type === "for_each_source" || type === "for_each_list" ||
    type === "json_object" || type === "json_array";
}

function xmlName(value: string): string {
  const safe = value.replace(/[^A-Za-z0-9_.-]/g, "_");
  return /^[A-Za-z_]/.test(safe) ? safe : `_${safe}`;
}

function emitXmlTextish(block: BlockNode, ctx: GoEmitContext): string[] {
  const inner = inputChild(block.inputs?.[XML_TEXT_INPUT]) ?? inputChild(block.inputs?.VALUE);
  const rendered = inner
    ? emitValueExpression(inner, ctx).join("")
    : String(block.fields?.TEXT ?? "");
  if (block.type === "xml_cdata") return [wrapXmlCdata(rendered)];
  return rendered ? [rendered] : [];
}

function emitXmlAttribute(block: BlockNode, ctx: GoEmitContext): string {
  const name = String(block.fields?.NAME ?? "").trim();
  if (!name) return "";
  const valBlock = inputChild(block.inputs?.[XML_TEXT_INPUT]) ?? inputChild(block.inputs?.VALUE);
  const val = valBlock ? emitValueExpression(valBlock, ctx).join("") : "";
  return ` ${name}="${val}"`;
}

function emitXmlDocument(block: BlockNode, ctx: GoEmitContext): string[] {
  const extra = block.extraState ?? {};
  const extras = extra.extras ?? [];
  const declaration = extra.declaration !== false &&
    (extras.length === 0 || extras.includes("declaration") || extra.declaration === true ||
      extra.version != null || block.fields?.VERSION != null);
  const lines: string[] = [];
  if (declaration) {
    lines.push(xmlDeclarationLine({
      version: String(block.fields?.VERSION ?? extra.version ?? "1.0"),
      encoding: String(block.fields?.ENCODING ?? extra.encoding ?? "UTF-8"),
      standalone: String(block.fields?.STANDALONE ?? extra.standalone ?? ""),
    }));
  }
  const root = inputChild(block.inputs?.[XML_ROOT_INPUT]) ?? firstChildStatement(block);
  if (!root) return lines;
  const emitted = emitStatementChain(root, ctx);
  const namespaces = namespacesFromDocument(block);
  if (namespaces.length && emitted[0]) {
    emitted[0] = injectXmlnsOnOpenTag(emitted[0], namespaces);
  }
  lines.push(...emitted);
  return lines;
}

function namespacesFromDocument(block: BlockNode): XmlNamespaceDecl[] {
  if (block.extraState?.namespaces?.length) return block.extraState.namespaces;
  const out: XmlNamespaceDecl[] = [];
  let i = 0;
  while (block.fields?.[`PREFIX_ns${i}`] !== undefined || block.fields?.[`URI_ns${i}`] !== undefined) {
    out.push({
      prefix: String(block.fields?.[`PREFIX_ns${i}`] ?? ""),
      uri: String(block.fields?.[`URI_ns${i}`] ?? ""),
    });
    i++;
  }
  return out;
}

function emitXmlElement(block: BlockNode, ctx: GoEmitContext): string[] {
  const tag = xmlTagName(block);
  const attrParts: string[] = [...emitStaticAttributes(block, ctx)];
  const inner: string[] = [];

  let attr = block.inputs?.[XML_ATTRIBUTES_INPUT]?.block;
  while (attr) {
    if (attr.type === "xml_attribute") attrParts.push(emitXmlAttribute(attr, ctx));
    attr = attr.next?.block;
  }

  const text = inputChild(block.inputs?.[XML_TEXT_INPUT]) ?? inputChild(block.inputs?.VALUE);
  if (text) inner.push(...emitXmlTextishOrValue(text, ctx));

  let current: BlockNode | undefined = block.inputs?.[XML_CHILDREN_INPUT]?.block ??
    (block.inputs?.[XML_ATTRIBUTES_INPUT] || text ? undefined : firstChildStatement(block));
  while (current) {
    inner.push(...emitBlock(current, ctx));
    current = current.next?.block;
  }

  const attrs = attrParts.join("");
  if (inner.length) return [`<${tag}${attrs}>`, ...inner, `</${tag}>`];
  return [`<${tag}${attrs} />`];
}

function emitXmlTextishOrValue(block: BlockNode, ctx: GoEmitContext): string[] {
  if (block.type === "xml_text" || block.type === "xml_cdata") return emitXmlTextish(block, ctx);
  return emitValueExpression(block, ctx);
}

function xmlTagName(block: BlockNode): string {
  const slot = String(block.fields?.SLOT_ID ?? "");
  const path = slot.includes(":") ? slot.slice(slot.indexOf(":") + 1) : slot;
  const fromPath = path.split("/").filter((part) => part && !part.startsWith("@")).pop();
  if (fromPath) return fromPath;
  const name = String(block.fields?.NAME ?? "element").trim();
  return name || "element";
}

function firstChildStatement(block: BlockNode): BlockNode | undefined {
  const inputs = block.inputs ?? {};
  if (inputs[XML_CHILDREN_INPUT]?.block) return inputs[XML_CHILDREN_INPUT].block;
  if (inputs[XML_ROOT_INPUT]?.block) return inputs[XML_ROOT_INPUT].block;
  for (const [key, value] of Object.entries(inputs)) {
    if (key.startsWith("TARGET_") && key !== XML_ATTRIBUTES_INPUT && value?.block) {
      return value.block;
    }
  }
  return undefined;
}

function emitStaticAttributes(block: BlockNode, ctx: GoEmitContext): string[] {
  const attrs: string[] = [];
  for (const attr of block.extraState?.attributes ?? []) {
    const name = String(attr.name ?? "").trim();
    if (!name) continue;
    attrs.push(` ${name}="${String(attr.value ?? "")}"`);
  }
  let i = 0;
  while (block.fields?.[`ATTR_NAME${i}`] !== undefined) {
    const name = String(block.fields[`ATTR_NAME${i}`]);
    const valBlock = inputChild(block.inputs?.[`ATTR_VALUE${i}`]);
    const val = valBlock
      ? emitValueExpression(valBlock, ctx).join("")
      : String(block.fields?.[`ATTR_VALUE${i}`] ?? "");
    attrs.push(` ${name}="${val}"`);
    i++;
  }
  return attrs;
}

function extractTextValue(block: BlockNode): string {
  if (block.type === "text") return String(block.fields?.TEXT ?? "");
  if (block.type === "text_code") return String(block.fields?.TEXT ?? "");
  return String(block.fields?.TEXT ?? block.fields?.EXPRESSION ?? "");
}

export function stripDelimiters(expr: string): string {
  return expr.replace(/^\{\{\s*/, "").replace(/\s*\}\}$/, "").replace(/^\{\{-?\s*/, "").replace(/\s*-?\}\}$/, "");
}

export function goQuote(s: string): string {
  return `"${s.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

export function sanitizeGoField(name: string): string {
  return name.replace(/[^a-zA-Z0-9_]/g, "_");
}

export function emitGoExpressionTemplate(
  ast: ExprAst,
  ctx: GoEmitContext = createGoEmitContext(),
): string {
  switch (ast.kind) {
    case "literal":
      if (typeof ast.value === "string") return goQuote(ast.value);
      return String(ast.value);
    case "binary":
      return `{{ ${emitGoExpr(ast, ctx)} }}`;
    case "call":
      if (ast.name === "concat") {
        return ast.args.map((arg) => emitGoExpressionTemplate(arg, ctx)).join("");
      }
      if (ast.name === "if") {
        const cond = emitGoExpr(ast.args[0]!, ctx);
        const thenPart = emitGoExpressionTemplate(ast.args[1]!, ctx);
        const elsePart = ast.args[2]
          ? emitGoExpressionTemplate(ast.args[2]!, ctx)
          : "";
        return `{{- if ${cond} -}}${thenPart}{{- else -}}${elsePart}{{- end -}}`;
      }
      return `{{ ${emitGoExpr(ast, ctx)} }}`;
  }
}

export function emitGoExpr(ast: ExprAst, ctx: GoEmitContext = createGoEmitContext()): string {
  switch (ast.kind) {
    case "literal":
      if (typeof ast.value === "string") return goQuote(ast.value);
      if (typeof ast.value === "boolean") return ast.value ? "true" : "false";
      return String(ast.value);
    case "binary": {
      const left = emitGoExpr(ast.left, ctx);
      const right = emitGoExpr(ast.right, ctx);
      const op = ast.op === "&&" ? "and" : ast.op === "||" ? "or" : ast.op;
      return `(${left} ${op} ${right})`;
    }
    case "call": {
      if (isQuantifyCall(ast.name)) return emitQuantifierGo(ast, ctx);
      const args = ast.args.map((arg) => emitGoExpr(arg, ctx));
      switch (ast.name) {
        case "xpathString":
        case "xpathNumber":
        case "xpathBoolean":
        case "xpathNode":
        case "xpath":
          return emitXPathGo(ast.args[0], ctx);
        case "maps_get": {
          const mapName = ast.args[0]?.kind === "literal" ? String(ast.args[0].value) : "defaults";
          const key = ast.args[1]?.kind === "literal" ? String(ast.args[1].value) : "";
          if (mapName === "defaults" || mapName === "Parameters") {
            return `.Parameters.${sanitizeGoField(key)}`;
          }
          return `index .${sanitizeGoField(mapName)} ${goQuote(key)}`;
        }
        case "trim":
          return `(${args[0]} | trim)`;
        case "change_case": {
          const mode = ast.args[1]?.kind === "literal" ? String(ast.args[1].value) : "LOWERCASE";
          if (mode === "UPPERCASE") return `(${args[0]} | upper)`;
          if (mode === "TITLECASE") return `(${args[0]} | title)`;
          return `(${args[0]} | lower)`;
        }
        case "concat":
          return args.join("");
        case "round":
          return `int ${args[0]}`;
        case "modulo":
          return `(${args[0]} % ${args[1]})`;
        case "constrain":
          return `(if ge ${args[0]} ${args[2]} then ${args[2]} else (if le ${args[0]} ${args[1]} then ${args[1]} else ${args[0]}))`;
        case "if":
          return `if ${args[0]} ${args[1]} ${args[2] ?? '""'}`;
        case "switch":
          return args[0] ?? '""';
        case "var": {
          const name = ast.args[0]?.kind === "literal" ? String(ast.args[0].value) : "";
          if (name && ctx.fnParams?.has(name)) return `.${sanitizeGoField(name)}`;
          return `.`;
        }
        case "call": {
          const fname = ast.args[0]?.kind === "literal" ? String(ast.args[0].value) : "";
          const fn = ctx.mappingFunctions?.find((item) => item.name === fname);
          const pairs: string[] = [];
          if (fn) {
            for (let i = 0; i < fn.params.length; i++) {
              pairs.push(goQuote(fn.params[i]!), args[i + 1] ?? `""`);
            }
          }
          // Action form (`{{template "name" pipeline}}`), not the forbidden `call` builtin.
          return `template ${goQuote(fname)} (dict ${pairs.join(" ")})`;
        }
        case "eq":
          return `(eq (${args[0]}) (${args[1]}))`;
        case "ne":
          return `(ne (${args[0]}) (${args[1]}))`;
        case "lt":
          return `(lt (${args[0]}) (${args[1]}))`;
        case "le":
          return `(le (${args[0]}) (${args[1]}))`;
        case "gt":
          return `(gt (${args[0]}) (${args[1]}))`;
        case "ge":
          return `(ge (${args[0]}) (${args[1]}))`;
        case "and":
          return `(and (${args[0]}) (${args[1]}))`;
        case "or":
          return `(or (${args[0]}) (${args[1]}))`;
        case "not":
          return `(not (${args[0]}))`;
        case "add":
          return `(${args[0]} + ${args[1]})`;
        case "subtract":
          return `(${args[0]} - ${args[1]})`;
        case "multiply":
          return `(${args[0]} * ${args[1]})`;
        case "divide":
          return `(${args[0]} / ${args[1]})`;
        case "list":
          return `[${args.join(", ")}]`;
        case "intersection":
        case "union":
        case "difference":
          return `/* ${ast.name} not available in Go template */`;
        case "sheet_get_cell":
        case "sheet_get_xy":
        case "sheet_get_row":
        case "sheet_get_column":
        case "sheet_get_header":
        case "sheet_get_data":
          return `/* ${ast.name} not available in Go template */`;
        case "sheet_lookup": {
          const name = args[0] ?? `""`;
          const col = args[1] ?? `""`;
          const val = args[2] ?? `""`;
          const ret = args[3] ?? `""`;
          return `sheetLookup ${name} ${col} ${val} ${ret}`;
        }
        case "decision_table": {
          const name = args[0] ?? `""`;
          const inputs = args[1] ?? "(dict)";
          const output = args[2] ?? `""`;
          return `decisionTable ${name} ${inputs} ${output}`;
        }
        case "handlebars":
          return `handlebars ${args[0] ?? '""'} ${args[1] ?? "(dict)"}`;
        case "map":
          return args.length ? `(dict ${args.join(" ")})` : "(dict)";
        default:
          return `/* unsupported: ${ast.name} */`;
      }
    }
  }
}

function emitXPathGo(pathArg: ExprAst | undefined, ctx: GoEmitContext): string {
  const path = pathArg?.kind === "literal" ? String(pathArg.value) : "";
  const named = namedLoopBinding(path, ctx.loopBindings);
  if (named) {
    if (named.rest === ".") return named.ident;
    return `(${goIndexChain(named.ident, named.rest)})`;
  }
  if (ctx.loopVar && isRelativeAuthoringPath(path)) {
    if (path === ".") return ".";
    return `(index . ${goQuote(path)})`;
  }
  if (path.startsWith("$.")) {
    return `(${jsonPathToGoIndex(path)})`;
  }
  return `(index .Data ${goQuote(path)})`;
}

/** JSONPath `$.a.b[0]['flat/key|value']` → chained `index .Data …`. */
export function jsonPathToGoIndex(path: string): string {
  const segments = jsonPathSegments(path);
  if (!segments.length) return `index .Data ""`;
  let expr = ".Data";
  for (const seg of segments) {
    expr = `index ${expr} ${goQuote(seg)}`;
  }
  return expr;
}

function jsonPathSegments(path: string): string[] {
  const body = path.replace(/^\$\.?/, "");
  const segments: string[] = [];
  let i = 0;
  while (i < body.length) {
    const ch = body[i];
    if (ch === ".") {
      i++;
      continue;
    }
    if (ch === "[") {
      const close = body.indexOf("]", i);
      if (close < 0) {
        segments.push(body.slice(i));
        break;
      }
      const inner = body.slice(i + 1, close).trim();
      if (
        (inner.startsWith("'") && inner.endsWith("'")) ||
        (inner.startsWith('"') && inner.endsWith('"'))
      ) {
        segments.push(inner.slice(1, -1).replace(/\\(['"\\])/g, "$1"));
      } else {
        segments.push(inner);
      }
      i = close + 1;
      continue;
    }
    let j = i;
    while (j < body.length && body[j] !== "." && body[j] !== "[") j++;
    if (j > i) segments.push(body.slice(i, j));
    i = j;
  }
  return segments.filter((seg) => seg.length > 0);
}

function emitQuantifierGo(ast: Extract<ExprAst, { kind: "call" }>, ctx: GoEmitContext): string {
  const args = ast.args.map((arg) => emitGoExpr(arg, ctx));
  return `/* ${ast.name}(${args.join(", ")}) */ false`;
}
